import { formatEncoded } from "@e308/ux";
import { pactFor } from "../diplomacy-state.js";
import { microFromString } from "../exact-numbers.js";
import { type TroopComposition, UNIT_IDS } from "../logistics-types.js";
import type { SeatObservation } from "../types.js";
import { name } from "./view-nodes.js";

export function seatName(o: SeatObservation, id: string): string {
  return id === "home"
    ? "Undeployed reserves"
    : (o.seats.find((s) => s.id === id)?.name ?? name(id));
}

export function troopsText(troops: TroopComposition): string {
  return UNIT_IDS.map(
    (unit) =>
      `${name(unit)} ${formatEncoded(troops[unit], { significantDigits: 4, plainDecimalPlaces: 2 })}`,
  ).join(" · ");
}

export function compactTroops(troops: TroopComposition): string {
  return ["Guards", "Raiders", "Guns"]
    .map(
      (label, index) =>
        `${label} ${formatEncoded(troops[UNIT_IDS[index] as (typeof UNIT_IDS)[number]], { significantDigits: 3, plainDecimalPlaces: 2 })}`,
    )
    .join(" · ");
}

export function frontHeadline(o: SeatObservation, id: string): string {
  const full = borderSituation(o, id);
  return full.split(". ")[0]?.replace(`${seatName(o, id)} `, "") ?? full;
}

export function borderSituation(o: SeatObservation, id: string): string {
  const enemy = o.seats.find((seat) => seat.id === id);
  const position = enemy?.logistics.borders[o.self.id];
  if (!enemy?.alive || !position) return "This border is no longer active.";
  const order = position.standingOrder;
  const label = seatName(o, id);
  const peace = o.match.borders.find(
    (b) => (b.left === id && b.right === o.self.id) || (b.right === id && b.left === o.self.id),
  );
  const remaining = Math.max(0, (peace?.fightingStartsAtMs ?? 0) - o.gameTimeMs);
  const pact = pactFor(o.diplomacy, o.self.id, id);
  if (remaining > 0)
    return `Opening peace: ${Math.ceil(remaining / 1_000)} game seconds remain. ${label} has ${name(order)} queued; no fighting yet.`;
  if (pact?.status === "active" || pact?.status === "notice")
    return `Pact ${pact.status === "active" ? "active" : "notice active"}: no fighting. ${label} has ${name(order)} queued.`;
  if (!UNIT_IDS.some((unit) => microFromString(position.troops[unit]) > 0n))
    return `${label} has no troops facing you. ${name(order)} is their standing order.`;
  if (order === "hold")
    return `${label} is holding. Their troops return fire if you attack; they do not attack your buildings.`;
  const report = enemy.combat.borders[o.self.id];
  const firing =
    report?.lastAttack &&
    [
      report.lastAttack.troopDamage,
      report.lastAttack.infrastructureDamage,
      report.lastAttack.shieldAbsorbed,
    ].some((value) => microFromString(value) > 0n);
  const target = name(report?.effectiveTarget ?? "headquarters");
  const units = UNIT_IDS.filter((unit) => microFromString(position.troops[unit]) > 0n)
    .map(name)
    .join(" + ");
  return `${label} ${firing ? (order === "raid" ? "is raiding you" : "is assaulting you") : `has ${name(order)} ordered (not firing)`} with ${units}. Infrastructure target: ${target}.`;
}
